const emptyValue = "--------";

const formatAmount = (value) => Number(value || 0).toFixed(2);

const csvTextValue = (value) =>
  value === null || value === undefined || value === "" ? emptyValue : `\t${value}`;

const formatDate = (value) =>
  value ? csvTextValue(String(value).slice(0, 10)) : emptyValue;

const formatDateTime = (value) => {
  if (!value) return emptyValue;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return csvTextValue(value);
  }

  return csvTextValue(date.toLocaleString());
};

const stockWithUnit = (stock, unit) =>
  stock || stock === 0 ? `${stock} ${unit || ""}`.trim() : emptyValue;

const quantityWithUnit = (quantity, unit) =>
  quantity || quantity === 0 ? `${quantity} ${unit || ""}`.trim() : emptyValue;

const safeFileNameSegment = (value) =>
  String(value || "report")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const flattenRow = (row, prefix = "") =>
  Object.entries(row || {}).reduce((flatRow, [key, value]) => {
    const flatKey = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      return { ...flatRow, ...flattenRow(value, flatKey) };
    }

    return {
      ...flatRow,
      [flatKey]: Array.isArray(value) ? JSON.stringify(value) : value,
    };
  }, {});

const escapeCsvValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return emptyValue;
  }

  const text = String(value);

  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
};

export const createVoidKotReportRows = (orders = []) =>
  orders.flatMap((order) =>
    (order.items || [])
      .filter(
        (item) =>
          item?.isCancelled === 1 ||
          item?.isCancelled === true ||
          item?.is_cancelled === 1 ||
          item?.is_cancelled === true,
      )
      .map((item) => ({
        ...item,
        orderDate: String(order.date || emptyValue).slice(0, 10),
        orderId: item.orderId || order.o_id || order.order_id || order.id,
      })),
  );

const withSerial = (rows = []) =>
  rows.map((row, index) => ({
    ...row,
    serial: index + 1,
  }));

const createOrderReportCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    "Order Dates": formatDate(row.date || row.orderDate),
    "Order Time": row.time || row.orderTime || emptyValue,
    "Order Id": row.o_id || row.order_id || row.id || emptyValue,
    Type: row.type || row.order_type || row.orderType || emptyValue,
    Table: row.table_label || row.table || row.table_no || emptyValue,
    "Order Status": row.order_status || emptyValue,
    "Payment Mode": row.payment_mode || emptyValue,
    "Sub-Total(Rs.)": formatAmount(row.amount || row.subTotal),
    "CGST(Rs.)": formatAmount(row.cgst),
    "SGST(Rs.)": formatAmount(row.sgst),
    "Discount(Rs.)": formatAmount(row.discount),
    "Via Cash(Rs.)": formatAmount(row.via_cash || row.viaCash),
    "Via Card(Rs.)": formatAmount(row.via_card || row.viaCard),
    "Via UPI(Rs.)": formatAmount(row.via_upi || row.viaUpi),
    "Grand Total(Round)": formatAmount(row.grand_total || row.grandTotal),
  }));

const createVoidKotReportCsvRows = (rows = []) =>
  withSerial(createVoidKotReportRows(rows)).map((row) => ({
    "S/N": row.serial,
    "Order Date": formatDate(row.orderDate),
    "Order Id": row.orderId || emptyValue,
    "KOT Id": row.kot_id || emptyValue,
    "Item Name": row.menu_item_name || emptyValue,
    Qty: row.quantity || emptyValue,
    "Price(Rs.)": formatAmount(row.price),
    "Cancelled By": row.cancelled_by || emptyValue,
    Reason: row.cancellation_reason || emptyValue,
  }));

const createCashierWaiterReportCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    "Cashier/Waiter": row.waiter || emptyValue,
    "Latest Date": formatDate(row.latest_date),
    "Table Count": row.table_count ?? emptyValue,
    Customers: row.total_customers ?? emptyValue,
    "Total Bills": row.total_bills ?? emptyValue,
    "Void Bills": row.void_bills ?? emptyValue,
    "Cash Income(Rs.)": formatAmount(row.cash_income),
    "Card Income(Rs.)": formatAmount(row.card_income),
    "UPI Income(Rs.)": formatAmount(row.upi_income),
    "Total Income(Rs.)": formatAmount(row.total_income),
  }));

const createCuisineReportCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    Cuisine: row.cuisine_name || emptyValue,
    "Menu Item Count": row.menu_item_count ?? emptyValue,
    "Total Sale(Rs.)": formatAmount(row.total_sale),
  }));

const createCategoryReportCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    Category: row.category_name || emptyValue,
    "Menu Item Count": row.menu_item_count ?? emptyValue,
    "Total Sale(Rs.)": formatAmount(row.total_sale),
  }));

const createItemReportCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    "Item Name": row.menu_name || emptyValue,
    "Item Count": row.item_count ?? emptyValue,
    "Total Sale(Rs.)": formatAmount(row.total_sale),
  }));

const createDiscountReportCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    "Order Date": formatDate(row.date),
    "Order Time": row.time || emptyValue,
    "Order Id": row.o_id || row.order_id || row.id || emptyValue,
    Table: row.table_label || row.table_no || emptyValue,
    "Sub-Total(Rs.)": formatAmount(row.amount),
    "Discount(Rs.)": formatAmount(row.discount),
    "Grand Total(Rs.)": formatAmount(row.grand_total),
  }));

const createGstReportCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    "Order Date": formatDate(row.date),
    "Order Id": row.o_id || row.order_id || row.id || emptyValue,
    "Taxable Amount(Rs.)": formatAmount(row.amount),
    "CGST(Rs.)": formatAmount(row.cgst),
    "SGST(Rs.)": formatAmount(row.sgst),
    "Grand Total(Rs.)": formatAmount(row.grand_total),
  }));

const createSectionReportCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    Section: row.section_name || emptyValue,
    Tables: row.total_tables ?? emptyValue,
    "Amount(Rs.)": formatAmount(row.total_amount),
    "CGST(Rs.)": formatAmount(row.total_cgst),
    "SGST(Rs.)": formatAmount(row.total_sgst),
    "Discount(Rs.)": formatAmount(row.total_discount),
    "Grand Total(Rs.)": formatAmount(row.total_grand_total),
  }));

const createCustomerReportCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    "Customer Name": row.first_name || emptyValue,
    Phone: row.phone || emptyValue,
    "Total Orders": row.total_orders ?? emptyValue,
    "Total Spent(Rs.)": formatAmount(row.total_spent),
    "Total Discount(Rs.)": formatAmount(row.total_discount),
  }));

export const createBillingReportCsvRows = (reportName, reportData = {}) => {
  const reportMappers = {
    "Order Report": () => createOrderReportCsvRows(reportData.orderReport),
    "Void KOT Report": () => createVoidKotReportCsvRows(reportData.orderReport),
    "Cashier/Waiter Report": () =>
      createCashierWaiterReportCsvRows(reportData.waiterReport),
    "Cuisine Report": () => createCuisineReportCsvRows(reportData.cuisineReport),
    "Category Report": () => createCategoryReportCsvRows(reportData.categoryReport),
    "Item Report": () => createItemReportCsvRows(reportData.itemReport),
    "Discount Report": () => createDiscountReportCsvRows(reportData.discountReport),
    "GST Report": () => createGstReportCsvRows(reportData.orderReport),
    "Section Report": () => createSectionReportCsvRows(reportData.sectionReport),
    "Customer Report": () => createCustomerReportCsvRows(reportData.customerReport),
  };

  return reportMappers[reportName]?.() || [];
};

const createInventoryStatusCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    "Last Updated": formatDateTime(row.lastUpdated),
    "Item Name": row.itemName || emptyValue,
    "Stock (Current/Original)": `${stockWithUnit(
      row.currentStock,
      row.unit,
    )} / ${stockWithUnit(row.originalStock, row.unit)}`,
    "Stock Status":
      Number(row.currentStock) <= Number(row.threshold) ? "Low Stock" : "In Stock",
  }));

const createInventoryPurchaseLogCsvRows = (rows = []) =>
  withSerial(rows).map((row) => ({
    "S/N": row.serial,
    "Update Time": formatDateTime(row.update_time),
    Ingredient: row.ingredient_name || emptyValue,
    Vendor: row.vendor_name || emptyValue,
    "Updated By": row.updated_by || emptyValue,
    Quantity: quantityWithUnit(row.new_quantity, row.unit),
    "Purchase Amount(Rs.)": row.purchase_amount ?? emptyValue,
  }));

export const createInventoryReportCsvRows = (reportName, reportData = {}) => {
  const reportMappers = {
    "Inventory Status": () =>
      createInventoryStatusCsvRows(reportData.inventoryStatus),
    "Inventory Purchase Log": () =>
      createInventoryPurchaseLogCsvRows(reportData.inventoryPurchaseLog),
  };

  return reportMappers[reportName]?.() || [];
};

export const buildReportCsvFileName = ({
  businessName,
  productName,
  reportName,
  startDate,
  endDate,
  includeDateRange = true,
}) => {
  const segments = [businessName, productName, reportName].map(safeFileNameSegment);

  if (includeDateRange) {
    segments.push(safeFileNameSegment(startDate), "to", safeFileNameSegment(endDate));
  }

  return `${segments.filter(Boolean).join("-")}.csv`;
};

export const downloadCsvFromJson = (rows = [], fileName = "report.csv") => {
  const flatRows = rows.map((row) => flattenRow(row));
  const headers = Array.from(
    flatRows.reduce((headerSet, row) => {
      Object.keys(row).forEach((key) => headerSet.add(key));
      return headerSet;
    }, new Set()),
  );

  const csvContent = headers.length
    ? [
        headers.map(escapeCsvValue).join(","),
        ...flatRows.map((row) =>
          headers.map((header) => escapeCsvValue(row[header])).join(","),
        ),
      ].join("\n")
    : "No data found\n";

  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
